// 가입 사용자 목록을 불러와 기사/관리자 권한을 부여하는 관리자 페이지
import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { getDocs, where } from "firebase/firestore";
import { orgQuery } from "@/lib/org";
import { useAuth } from "@/store/auth";
import { useOrgStore } from "@/store/org";
import clsx from "clsx";

import { plainError } from "@/lib/plainError";
interface UserRow {
  uid: string;
  email: string;
  displayName: string;
  role: "admin" | "driver" | null;
  creationTime: string;
  lastSignInTime: string;
}

export default function UserRoles() {
  const { user } = useAuth();
  // 대표 계정 = 업체를 만든 계정. 대표 줄은 아무도 못 바꾸고, 관리자 지정은 대표만 한다(서버도 같은 규칙, 대표 결정 Q8)
  const ownerUid = useOrgStore((s) => s.org?.createdBy) || "";
  const canAppointAdmin = !ownerUid || ownerUid === user?.uid;
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>("");
  const [err, setErr] = useState<string>("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const fn = httpsCallable(functions, "listUsers");
      const res = (await fn()) as { data: { users: UserRow[] } };
      // 권한 없음 → 기사 → 관리자 순, 그 안에서 최근 로그인 순
      const order = { null: 0, driver: 1, admin: 2 } as const;
      const sorted = [...res.data.users].sort((a, b) => {
        const ra = order[(a.role ?? "null") as keyof typeof order];
        const rb = order[(b.role ?? "null") as keyof typeof order];
        if (ra !== rb) return ra - rb;
        return (b.lastSignInTime || "").localeCompare(a.lastSignInTime || "");
      });
      setUsers(sorted);
    } catch (e: any) {
      setErr(plainError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadInvites();
  }, []);

  const [invite, setInvite] = useState<{ url: string; days: number } | null>(null);
  const [copied, setCopied] = useState<"" | "ok" | "fail">("");
  const [invites, setInvites] = useState<{ code: string; expiresAt: number; createdAt: number }[]>([]);

  // 살아 있는 초대 링크 목록(우리 업체, 안 끈 것, 기간 안) — 유출됐거나 퇴사하면 여기서 끈다
  const loadInvites = async () => {
    try {
      const snap = await getDocs(orgQuery("invites", where("revoked", "==", false)));
      const now = Date.now();
      setInvites(
        snap.docs
          .map((d) => ({ code: d.id, expiresAt: d.get("expiresAt")?.toMillis?.() ?? 0, createdAt: d.get("createdAt")?.toMillis?.() ?? 0 }))
          .filter((i) => i.expiresAt > now)
          .sort((a, b) => b.createdAt - a.createdAt),
      );
    } catch { /* 목록은 부가 정보 — 못 읽어도 화면은 뜬다 */ }
  };
  const revoke = async (code: string) => {
    if (!confirm("이 초대 링크를 끌까요? 끄면 이 링크로는 아무도 들어올 수 없습니다.")) return;
    setBusy("invite");
    try {
      await httpsCallable<{ code: string }, { ok: boolean }>(functions, "revokeInvite")({ code });
      if (invite?.url.endsWith(code)) setInvite(null);
      await loadInvites();
    } catch (e: any) {
      setErr(plainError(e));
    } finally {
      setBusy("");
    }
  };

  // 기사 초대 링크 — 연 사람은 우리 업체 기사로 자동 등록된다(14일 유효, 여러 명 사용 가능)
  const makeInvite = async () => {
    setBusy("invite");
    setErr("");
    try {
      const fn = httpsCallable<{ role: string }, { code: string }>(functions, "createInvite");
      const res = await fn({ role: "driver" });
      setInvite({ url: `${window.location.origin}/join/${res.data.code}`, days: 14 });
      setCopied("");
      await loadInvites();
    } catch (e: any) {
      setErr(plainError(e));
    } finally {
      setBusy("");
    }
  };

  const grant = async (u: UserRow, role: "admin" | "driver" | "none") => {
    if (u.role === role || (role === "none" && !u.role)) return;
    // 서버 기능은 바로 막히지만, 이미 켜 둔 화면의 목록 보기는 로그인 표가 끝날 때(최대 1시간)까지 남는다 — 실제 동작대로 알린다
    if (role === "none" && !confirm(`${u.displayName || u.email} 님의 권한을 뺄까요? 이미 켜 둔 화면은 최대 1시간 안에 끊깁니다.`)) return;
    setBusy(u.uid);
    setErr("");
    try {
      const fn = httpsCallable(functions, "setUserRole");
      await fn({ uid: u.uid, role, displayName: u.displayName || u.email });
      await load();
    } catch (e: any) {
      setErr(plainError(e));
    } finally {
      setBusy("");
    }
  };

  const fmtDate = (s: string) => (s ? new Date(s).toLocaleDateString("ko-KR") : "—");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">🔐 사용자 권한 관리</h1>
        <button className="btn-ghost text-sm" onClick={load} disabled={loading}>
          새로고침
        </button>
      </div>

      <p className="text-sm text-slate-500">
        기사님은 아래 초대 링크로 들어옵니다. 표에는 우리 업체 사람만 보이고, 권한을 바꾸면 그 사람이 <strong>다시 로그인할 때</strong> 적용됩니다.
        {!canAppointAdmin && " 관리자 지정은 대표 계정(업체를 만든 계정)만 할 수 있습니다."}
      </p>

      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-bold">🚚 기사님 초대하기</div>
            <p className="text-sm text-slate-500">링크를 만들어 카톡이나 문자로 보내세요. 기사님이 링크를 열고 구글 로그인한 뒤 「등록」을 누르면 우리 업체 기사로 등록됩니다.</p>
          </div>
          <button className="btn-primary" onClick={makeInvite} disabled={busy === "invite"}>
            {busy === "invite" ? "만드는 중…" : "초대 링크 만들기"}
          </button>
        </div>
        {invite && (
          <div className="rounded-xl bg-milk-50 p-3 space-y-2">
            <div className="text-sm break-all font-mono">{invite.url}</div>
            <div className="flex gap-2 flex-wrap">
              <button
                className="btn-secondary text-sm"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(invite.url); setCopied("ok"); }
                  catch { setCopied("fail"); }
                }}
              >
                {copied === "ok" ? "복사됨 ✓" : copied === "fail" ? "복사 안 됨 — 링크를 길게 눌러 복사하세요" : "링크 복사"}
              </button>
              {typeof navigator.share === "function" && (
                <button className="btn-secondary text-sm" onClick={() => navigator.share({ title: "무상우유콕 기사 등록", text: "[무상우유콕] 기사 등록 링크입니다. 눌러서 구글 로그인하면 바로 배송 화면이 열립니다.", url: invite.url }).catch(() => {})}>
                  카톡 등으로 보내기
                </button>
              )}
              <a className="btn-secondary text-sm" href={`sms:?&body=${encodeURIComponent(`[무상우유콕] 기사 등록 링크입니다. 눌러서 구글 로그인하면 바로 배송 화면이 열립니다.
${invite.url}`)}`}>
                문자로 보내기
              </a>
            </div>
            <p className="text-xs text-slate-500">{invite.days}일 동안 쓸 수 있고, 여러 기사님이 같은 링크로 들어올 수 있습니다. 링크를 연 사람은 「등록」을 한 번 더 눌러야 등록됩니다.</p>
          </div>
        )}
        {invites.length > 0 && (
          <div className="text-sm">
            <div className="font-semibold mb-1">살아 있는 초대 링크 {invites.length}개</div>
            <ul className="divide-y">
              {invites.map((i) => (
                <li key={i.code} className="py-2 flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono text-xs break-all">…/join/{i.code}</span>
                  <span className="text-slate-500">{new Date(i.expiresAt).toLocaleDateString("ko-KR")}까지</span>
                  <button className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-white text-rose-600 hover:bg-rose-50 min-h-[36px]" onClick={() => revoke(i.code)} disabled={busy === "invite"}>끄기</button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-1">링크가 밖으로 새었거나 기사님이 그만두면 여기서 끄세요. 끈 링크로는 아무도 못 들어옵니다.</p>
          </div>
        )}
      </div>

      {err && (
        <div className="card p-3 text-sm text-rose-600 bg-rose-50 border border-rose-200">{err}</div>
      )}

      <div className="card">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3">사용자</th>
              <th className="p-3">현재 권한</th>
              <th className="p-3">가입 / 최근 로그인</th>
              <th className="p-3 text-right">권한 부여</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.uid === user?.uid;
              const isOwner = !!ownerUid && u.uid === ownerUid;
              const locked = isMe || isOwner; // 자기 자신·대표 줄은 권한을 바꾸지 않는다
              return (
                <tr key={u.uid} className="border-t">
                  <td className="p-3">
                    <div className="font-medium">
                      {u.displayName || u.email || u.uid.slice(0, 8)}
                      {isMe && <span className="ml-1 text-xs text-slate-400">(나)</span>}
                      {isOwner && <span className="ml-1 chip bg-slate-100 text-slate-600">대표</span>}
                    </div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </td>
                  <td className="p-3">
                    {u.role ? (
                      <span
                        className={clsx(
                          "chip",
                          u.role === "admin" ? "bg-rose-100 text-rose-700" : "bg-milk-100 text-milk-700",
                        )}
                      >
                        {u.role === "admin" ? "관리자" : "기사"}
                      </span>
                    ) : (
                      <span className="chip bg-amber-100 text-amber-700">권한 대기</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-500">
                    {fmtDate(u.creationTime)} / {fmtDate(u.lastSignInTime)}
                  </td>
                  <td className="p-3 text-right">
                    {locked ? (
                      <span className="text-xs text-slate-400">{isOwner ? "대표 계정은 바꿀 수 없습니다" : "내 권한은 다른 관리자가 바꿉니다"}</span>
                    ) : (
                    <div className="inline-flex gap-1">
                      <button
                        className={clsx(
                          "px-3 py-1.5 rounded-lg text-xs font-semibold border",
                          u.role === "driver"
                            ? "bg-milk-500 text-white border-milk-500"
                            : "bg-white text-slate-600 hover:bg-milk-50",
                        )}
                        disabled={busy === u.uid}
                        onClick={() => grant(u, "driver")}
                      >
                        기사
                      </button>
                      <button
                        className={clsx(
                          "px-3 py-1.5 rounded-lg text-xs font-semibold border",
                          u.role === "admin"
                            ? "bg-rose-500 text-white border-rose-500"
                            : "bg-white text-slate-600 hover:bg-rose-50",
                        )}
                        disabled={busy === u.uid || (!canAppointAdmin && u.role !== "admin")}
                        title={!canAppointAdmin && u.role !== "admin" ? "관리자 지정은 대표 계정만 할 수 있습니다" : undefined}
                        onClick={() => grant(u, "admin")}
                      >
                        관리자
                      </button>
                      {u.role && (
                        <button
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-white text-slate-500 hover:bg-slate-50"
                          disabled={busy === u.uid}
                          onClick={() => grant(u, "none")}
                        >
                          빼기
                        </button>
                      )}
                    </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-slate-400">
                  가입한 사용자가 없습니다
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-slate-400">
                  불러오는 중…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
